import React, { useEffect } from 'react';
import { Box, Button, Stack, Typography, Skeleton } from '@mui/material';
import { LineChart } from '@mui/x-charts';
import { axisClasses } from '@mui/x-charts/ChartsAxis';
import dayjs from '@/config/dayjs';
import { useLocalization } from '@/hooks/useLocalization';
import { useAssetAllocationChartData } from './useAssetAllocationChartData';
import {
  AssetAllocationChartTooltip,
  ChartDataContext,
} from './AssetAllocationChartTooltip';
import {
  DisplayMode,
  DISPLAY_DATE_FORMAT,
  formatAllocationValue,
  formatSeriesValue,
} from './assetAllocationChartUtils';
import {
  formatChartDateAxis,
  formatPercentAxis,
  getCurrencyStringTitleForChart,
  responsiveTickInterval,
} from '@/utils/charts/axisFormatters';
import { useViewportWidth } from '@/hooks/useViewportWidth';
import { APP_MONTH_DAY_FORMAT } from '@/constants/dateFormats';

const CHART_HEIGHT = 550;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Labels every tick, for an axis whose unlabelled ticks are already filtered out. */
const labelEveryTick = (): boolean => true;

/** Which ticks the time axis keeps, and which of those it labels. */
interface TimeAxisTickFilters {
  tickInterval?: (value: Date | null, index: number) => boolean;
  tickLabelInterval: (value: Date | null, index: number) => boolean;
}

/**
 * The page draws every tick and thins only the labels on a narrow screen. A
 * compact tile drops the ticks it would leave unlabelled, so no mark is bare.
 */
function timeAxisTickFilters(
  labelledTicks: (value: Date | null, index: number) => boolean,
  compact: boolean,
): TimeAxisTickFilters {
  if (!compact) return { tickLabelInterval: labelledTicks };
  return { tickInterval: labelledTicks, tickLabelInterval: labelEveryTick };
}

/** The page's value axis and side margins, sized for a full-width chart. */
const PAGE_AXES = { yAxisWidth: 100, margin: { left: 50, right: 50 } };

/**
 * A tile's value axis and side margins: just room for "-$10M" on the left and
 * half of the last date label on the right, so the plot gets the width.
 */
const COMPACT_AXES = { yAxisWidth: 60, margin: { left: 8, right: 28 } };

/**
 * A tick's label. A range of three months or less puts ticks on days, so it
 * names the day; month-only labels repeated the month under every tick.
 */
function formatDateTick(
  value: Date,
  ctx: { location: string },
  isLongRange: boolean,
): string {
  if (isLongRange || ctx.location === 'tooltip') {
    return formatChartDateAxis(value, ctx);
  }
  return dayjs(value).format(APP_MONTH_DAY_FORMAT);
}

interface AssetAllocationChartProps {
  startDate: Date;
  endDate: Date;
  displayMode: DisplayMode;
  onDataLoaded?: () => void;
  excludedAccounts?: number[];
  excludedCategories?: number[];
  excludedAccountTypes?: number[];
  excludedInstitutions?: number[];
  investorId?: number;
  investorScoped?: boolean;
  /** Measured width of the host container. Overrides the window width for axis tick spacing. */
  containerWidth?: number;
  /** Chart height in pixels. Defaults to the full-page height. */
  height?: number;
  /** Reports whether the chart is waiting on data. */
  onChartUpdatingChange?: (updating: boolean) => void;
  /** Adds a Retry button to the error state, as the advisor widgets have. */
  showRetry?: boolean;
  /**
   * On a day-ticked range (three months or less) the time axis spans the
   * picked range instead of the data, so a range holding one or two points
   * still shows its dates. Ticks never fall closer than a day apart.
   */
  axisSpansRange?: boolean;
  /**
   * Narrows the value axis and side margins so a dashboard tile's plot uses
   * its width. Ticks a narrow chart leaves unlabelled are dropped rather than
   * drawn bare, so every tick mark has its date.
   */
  compactAxes?: boolean;
}

function ChartSkeleton({ height = CHART_HEIGHT }: { height?: number }) {
  return (
    <Stack
      direction='column'
      gap={2}
    >
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='center'
      >
        <Skeleton
          variant='text'
          width={180}
          height={24}
        />
        <Skeleton
          variant='text'
          width={180}
          height={24}
        />
      </Box>
      <Skeleton
        variant='rounded'
        height={height}
        sx={{ width: '100%' }}
      />
    </Stack>
  );
}

const AssetAllocationChart: React.FC<AssetAllocationChartProps> = ({
  startDate,
  endDate,
  displayMode,
  onDataLoaded,
  excludedAccounts,
  excludedCategories,
  excludedAccountTypes,
  excludedInstitutions,
  investorId,
  investorScoped,
  containerWidth,
  height,
  onChartUpdatingChange,
  showRetry = false,
  axisSpansRange = false,
  compactAxes = false,
}) => {
  const { t } = useLocalization();
  const viewportWidth = useViewportWidth();
  const {
    dataSet,
    allCategoryNames,
    categoryColorMap,
    startTotalValue,
    startTotalDate,
    endTotalValue,
    endTotalDate,
    graphColors,
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useAssetAllocationChartData({
    startDate,
    endDate,
    displayMode,
    onDataLoaded,
    excludedAccounts,
    excludedCategories,
    excludedAccountTypes,
    excludedInstitutions,
    investorId,
    investorScoped,
  });

  useEffect(() => {
    onChartUpdatingChange?.(isLoading);
  }, [isLoading, onChartUpdatingChange]);

  const tickWidth = containerWidth ?? viewportWidth;
  const chartHeight = height ?? CHART_HEIGHT;

  const renderError = () => {
    const message = t('assetAllocation.errorLoadingHistoricalChart');
    if (!showRetry) return <Box>{message}</Box>;
    return (
      <Stack
        alignItems='flex-start'
        gap={1.5}
      >
        <Typography color='error'>{message}</Typography>
        <Button
          variant='outlined'
          disabled={isFetching}
          onClick={() => {
            refetch();
          }}
        >
          {t('retry', 'Retry')}
        </Button>
      </Stack>
    );
  };

  if (isLoading) return <ChartSkeleton height={chartHeight} />;
  if (isError) return renderError();
  if (!dataSet.length) return <Box>{t('assetAllocation.noDataAvailable')}</Box>;

  const rangeMs = endDate.getTime() - startDate.getTime();
  const msPerMonthApprox = 30 * 24 * 60 * 60 * 1000;
  const isLongRange = rangeMs > 3 * msPerMonthApprox;

  const longRangeMinStep = 28 * 24 * 60 * 60 * 1000;
  let tickMinStep: number | undefined;
  if (containerWidth !== undefined && rangeMs > 0) {
    const MIN_PX_PER_TICK_LABEL = 70;
    const maxTickLabels = Math.max(
      1,
      Math.floor(containerWidth / MIN_PX_PER_TICK_LABEL),
    );
    const widthDerivedMinStep = rangeMs / maxTickLabels;
    tickMinStep = isLongRange
      ? Math.max(longRangeMinStep, widthDerivedMinStep)
      : widthDerivedMinStep;
  } else {
    tickMinStep = isLongRange ? longRangeMinStep : undefined;
  }

  const axes = compactAxes ? COMPACT_AXES : PAGE_AXES;
  const spansRange = axisSpansRange && !isLongRange;
  if (spansRange) {
    tickMinStep = Math.max(MS_PER_DAY, tickMinStep ?? 0);
  }
  const labelledTicks = responsiveTickInterval(tickWidth);
  const tickFilters = timeAxisTickFilters(labelledTicks, compactAxes);

  const series = allCategoryNames.map((catName, index) => ({
    dataKey: catName,
    showMark: true,
    area: true,
    color: categoryColorMap[catName] ?? graphColors[index % graphColors.length],
    valueFormatter: (value: number | null) =>
      formatSeriesValue(value, displayMode),
    label: catName,
    stack: 'Allocation',
  }));

  return (
    <Stack
      direction='column'
      gap={2}
    >
      <Box
        display='flex'
        justifyContent='space-between'
      >
        <Typography variant='body2'>
          {t('assetAllocation.start')}:{' '}
          {dayjs(startTotalDate).format(DISPLAY_DATE_FORMAT)} <br />
          {t('assetAllocation.totalValue')}:{' '}
          {formatAllocationValue(startTotalValue, displayMode)}
        </Typography>
        <Typography variant='body2'>
          {t('assetAllocation.end')}:{' '}
          {dayjs(endTotalDate).format(DISPLAY_DATE_FORMAT)} <br />
          {t('assetAllocation.totalValue')}:{' '}
          {formatAllocationValue(endTotalValue, displayMode)}
        </Typography>
      </Box>

      <ChartDataContext.Provider value={dataSet}>
        <LineChart
          height={chartHeight}
          colors={graphColors}
          dataset={dataSet}
          series={series}
          xAxis={[
            {
              dataKey: 'date',
              scaleType: 'time',
              height: 50,
              position: 'bottom',
              tickLabelStyle: { fontSize: 12 },
              domainLimit: 'strict',
              ...(spansRange && { min: startDate, max: endDate }),
              valueFormatter: (value: Date, ctx) =>
                formatDateTick(value, ctx, isLongRange),
              ...tickFilters,
              tickMinStep,
            },
          ]}
          yAxis={[
            {
              width: axes.yAxisWidth,
              ...(displayMode === 'percentage' && { min: 0, max: 100 }),
              valueFormatter: (value: number) =>
                displayMode === 'percentage'
                  ? formatPercentAxis(value)
                  : getCurrencyStringTitleForChart(value),
            },
          ]}
          margin={{ ...axes.margin, bottom: 50 }}
          sx={{
            '& .MuiAreaElement-root': { fillOpacity: 0.5 },
            [`.${axisClasses.directionX} .${axisClasses.tickLabel}`]: {
              visibility: 'visible',
              fontSize: 12,
            },
          }}
          slots={{ tooltip: AssetAllocationChartTooltip }}
          slotProps={{
            tooltip: { trigger: 'axis' },
            legend: {
              position: { vertical: 'bottom', horizontal: 'center' },
              direction: 'horizontal',
              sx: {
                paddingTop: 2,
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: 1,
              },
            },
          }}
        />
      </ChartDataContext.Provider>
    </Stack>
  );
};

export default AssetAllocationChart;
